/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { exportCustomers, exportTransactions } from "../../api/queries";
import { useLanguage } from "../../contexts/LanguageContext";
import { Modal } from "./ui";
import { buttonClass, chipClass, inputClass, labelClass } from "./styles";

const startOfDay = (d) => {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
};

const endOfDay = (d) => {
  const r = new Date(d);
  r.setHours(23, 59, 59, 999);
  return r;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

// Ranges in the merchant's local time zone; the request carries them as UTC
// ISO timestamps. "All time" sends no bounds.
const EXPORT_PRESETS = [
  { id: "today", range: () => [startOfDay(new Date()), endOfDay(new Date())] },
  { id: "yesterday", range: () => [startOfDay(daysAgo(1)), endOfDay(daysAgo(1))] },
  { id: "last7Days", range: () => [startOfDay(daysAgo(6)), endOfDay(new Date())] },
  { id: "last30Days", range: () => [startOfDay(daysAgo(29)), endOfDay(new Date())] },
  {
    id: "thisMonth",
    range: () => {
      const d = new Date();
      return [startOfDay(new Date(d.getFullYear(), d.getMonth(), 1)), endOfDay(d)];
    },
  },
  {
    id: "lastMonth",
    range: () => {
      const d = new Date();
      return [
        startOfDay(new Date(d.getFullYear(), d.getMonth() - 1, 1)),
        endOfDay(new Date(d.getFullYear(), d.getMonth(), 0)),
      ];
    },
  },
  {
    id: "thisYear",
    range: () => {
      const d = new Date();
      return [startOfDay(new Date(d.getFullYear(), 0, 1)), endOfDay(d)];
    },
  },
  { id: "allTime", range: () => [null, null] },
];

const EXPORT_TYPES = [
  { id: "customers", label: "customers.export.panel.typeCustomers", hint: "customers.export.panel.typeCustomersHint" },
  { id: "transactions", label: "customers.export.panel.typeTransactions", hint: "customers.export.panel.typeTransactionsHint" },
];

// "2026-10-01" from a date input, as local midnight.
const localDay = (value) => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const todayValue = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function ExportDialog({ open, storeId, onClose }) {
  const { t } = useLanguage();
  const [type, setType] = useState("customers");
  const [preset, setPreset] = useState("last30Days");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const close = () => {
    if (exporting) return;
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!storeId) return;

    let start;
    let end;
    if (preset === "custom") {
      if (!from || !to) {
        setError(t("customers.export.errors.rangeRequired"));
        return;
      }
      start = startOfDay(localDay(from));
      end = endOfDay(localDay(to));
      if (start > end) {
        setError(t("customers.export.errors.invalidRange"));
        return;
      }
    } else {
      [start, end] = EXPORT_PRESETS.find((p) => p.id === preset).range();
    }

    setError(null);
    setExporting(true);
    try {
      const range = {
        startAt: start ? start.toISOString() : null,
        endAt: end ? end.toISOString() : null,
      };
      if (type === "transactions") {
        await exportTransactions(storeId, range);
      } else {
        await exportCustomers(storeId, range);
      }
      onClose();
    } catch (err) {
      console.error("Export failed:", err);
      setError(t("customers.export.errors.failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="max-w-[520px]"
      title={t("customers.export.panel.title")}
      closeLabel={t("common.close")}
      footer={
        <>
          <button type="button" onClick={close} className={buttonClass.secondary}>
            {t("common.cancel")}
          </button>
          <button type="button" onClick={submit} disabled={exporting} className={buttonClass.primary}>
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
            {exporting ? t("customers.export.panel.exporting") : t("customers.export.panel.submit")}
          </button>
        </>
      }
    >
      <div
        role="radiogroup"
        aria-label={t("customers.export.panel.typeLabel")}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {EXPORT_TYPES.map((option) => {
          const on = option.id === type;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setType(option.id)}
              className={`flex flex-col items-start gap-1 rounded-xl p-3.5 text-left ${
                on ? "border-2 border-indigo-600 bg-indigo-50/50" : "border border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <span className="text-sm font-semibold text-gray-900">{t(option.label)}</span>
              <span className="text-xs leading-snug text-gray-600">{t(option.hint)}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-2.5">
        <span className="text-[13px] font-semibold text-gray-700">{t("customers.export.panel.dateRange")}</span>
        <div role="group" aria-label={t("customers.export.panel.dateRange")} className="flex flex-wrap gap-1.5">
          {[...EXPORT_PRESETS.map((p) => p.id), "custom"].map((id) => (
            <button
              key={id}
              type="button"
              aria-pressed={preset === id}
              onClick={() => {
                setPreset(id);
                setError(null);
              }}
              className={chipClass(preset === id)}
            >
              {t(`customers.export.panel.presets.${id}`)}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex flex-wrap gap-3">
            <label className={`${labelClass} flex-[1_1_180px]`}>
              {t("customers.export.panel.from")}
              <input
                type="date"
                name="export-from"
                max={todayValue()}
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setError(null);
                }}
                className={inputClass}
              />
            </label>
            <label className={`${labelClass} flex-[1_1_180px]`}>
              {t("customers.export.panel.to")}
              <input
                type="date"
                name="export-to"
                max={todayValue()}
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setError(null);
                }}
                className={inputClass}
              />
            </label>
          </div>
        )}
        <span className="text-xs text-gray-600">
          {t("customers.export.panel.timezoneHint", { timezone })}
        </span>
      </div>

      {error && (
        <p role="alert" className="m-0 rounded-[10px] bg-red-50 px-3 py-2.5 text-sm text-red-700">
          {error}
        </p>
      )}
    </Modal>
  );
}
